import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

function Cohorts() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [ownerCohorts, setOwnerCohorts] = useState([]);
  const [filteredOwner, setFilteredOwner] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(true);

  const [invitedCohorts, setInvitedCohorts] = useState([]);
  const [invitedLoading, setInvitedLoading] = useState(true);

  const [invites, setInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(true);

  const [tab, setTab] = useState("cohorts");
  const [search, setSearch] = useState("");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const loadOwnerCohorts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(
        collection(db, "cohorts"),
        where("evaluatorId", "==", user.uid)
      );
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setOwnerCohorts(list);
      setFilteredOwner(list);
    } catch (err) {
      console.error("Failed to load owner cohorts", err);
    } finally {
      setOwnerLoading(false);
    }
  };

  const loadInvitedCohorts = async () => {
    if (!user?.uid) return;
    try {
      const memberSnap = await getDocs(
        query(collection(db, "cohortMembers"), where("userId", "==", user.uid))
      );
      const cohortIds = memberSnap.docs.map((d) => d.data().cohortId);
      const cohortSnaps = await Promise.all(
        cohortIds.map((id) => getDoc(doc(db, "cohorts", id)))
      );
      const results = cohortSnaps
        .filter((s) => s.exists())
        .map((s) => ({ id: s.id, ...s.data() }));
      setInvitedCohorts(results);
    } catch (err) {
      console.error("Failed to load invited cohorts", err);
    } finally {
      setInvitedLoading(false);
    }
  };

  const loadInvites = async () => {
    if (!user?.uid) return;
    try {
      const q = query(
        collection(db, "cohortInvites"),
        where("invitedUserId", "==", user.uid),
        where("status", "==", "pending")
      );
      const snap = await getDocs(q);
      setInvites(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to load invites", err);
    } finally {
      setInviteLoading(false);
    }
  };

  useEffect(() => {
    loadOwnerCohorts();
    loadInvitedCohorts();
    loadInvites();
  }, [user?.uid]);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredOwner(ownerCohorts);
      return;
    }
    const q = search.toLowerCase();
    setFilteredOwner(
      ownerCohorts.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.description?.toLowerCase().includes(q)
      )
    );
  }, [search, ownerCohorts]);

  const createCohort = async () => {
    setError("");
    if (!name.trim()) {
      setError("Cohort name is required.");
      return;
    }
    try {
      setCreating(true);
      await addDoc(collection(db, "cohorts"), {
        evaluatorId: user.uid,
        name: name.trim(),
        description: description.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setName("");
      setDescription("");
      await loadOwnerCohorts();
    } catch (err) {
      console.error(err);
      setError("Failed to create cohort.");
    } finally {
      setCreating(false);
    }
  };

  const deleteCohort = async (cohortId) => {
    const ok = window.confirm(
      "Delete this cohort? This will not delete any startups."
    );
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "cohorts", cohortId));
      setOwnerCohorts((prev) => prev.filter((c) => c.id !== cohortId));
      setFilteredOwner((prev) => prev.filter((c) => c.id !== cohortId));
    } catch {
      alert("Failed to delete cohort.");
    }
  };

  const acceptInvite = async (invite) => {
    try {
      await addDoc(collection(db, "cohortMembers"), {
        cohortId: invite.cohortId,
        userId: user.uid,
        email: user.email,
        name: user.displayName || "Evaluator",
        ownerEvaluatorId: invite.senderEvaluatorId,
        role: "evaluator",
        joinedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "cohortInvites", invite.id), {
        status: "accepted",
      });
      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
      await loadInvitedCohorts();
    } catch (err) {
      console.error(err);
      alert("Failed to accept invite.");
    }
  };

  const rejectInvite = async (inviteId) => {
    try {
      await updateDoc(doc(db, "cohortInvites", inviteId), {
        status: "rejected",
      });
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
    } catch (err) {
      console.error(err);
      alert("Failed to reject invite.");
    }
  };

  return (
    <div>
      {/* HERO HEADER */}
      <div
        title="Cohorts let multiple evaluators collaborate on reviewing startups."
        style={{
          background: "linear-gradient(135deg,#1e293b,#0f172a)",
          padding: "28px 28px",
          borderRadius: 16,
          color: "white",
          marginBottom: 28,
          boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 28 }}>Evaluator Cohorts</h1>
        <p style={{ marginTop: 6, opacity: 0.85 }}>
          Collaborate with other evaluators to review and rank startups together.
        </p>
      </div>

      {/* TABS */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 28,
          background: "#f1f5f9",
          padding: 6,
          borderRadius: 12,
          width: "fit-content",
        }}
      >
        {[
          { key: "cohorts", label: "My Cohorts", tip: "Cohorts you created and manage." },
          { key: "invited", label: "Invited Cohorts", tip: "Cohorts where you are a member but not the owner." },
          { key: "invites", label: `Invites ${invites.length ? `(${invites.length})` : ""}`, tip: "Pending invitations awaiting your response." },
        ].map((t) => (
          <button
            key={t.key}
            title={t.tip}
            onClick={() => setTab(t.key)}
            style={{
              padding: "8px 16px",
              borderRadius: 10,
              border: "none",
              fontWeight: 600,
              cursor: "pointer",
              background: tab === t.key ? "#0f172a" : "transparent",
              color: tab === t.key ? "#fff" : "#0f172a",
              transition: "all 0.2s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* INVITES */}
      {tab === "invites" && (
        <div className="card" style={{ padding: 28 }}>
          <h3 title="Invitations from other evaluators to join their cohorts.">
            Pending Invites
          </h3>
          {inviteLoading ? (
            <p>Loading invites…</p>
          ) : invites.length === 0 ? (
            <p style={{ color: "#64748b" }}>No pending invites.</p>
          ) : (
            invites.map((i) => (
              <div
                key={i.id}
                style={{
                  padding: 18,
                  borderRadius: 12,
                  marginTop: 16,
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <strong style={{ fontSize: 16 }}>{i.cohortName}</strong>
                <p style={{ color: "#64748b", margin: "4px 0" }}>{i.cohortDescription}</p>
                <p style={{ fontSize: 13 }}>Invited by: {i.senderName}</p>
                <div style={{ marginTop: 12 }}>
                  <button
                    className="btn-primary"
                    title="Join this cohort and collaborate on startup evaluations."
                    onClick={() => acceptInvite(i)}
                  >
                    Accept
                  </button>
                  <button
                    className="btn-secondary"
                    title="Decline this invitation."
                    style={{ marginLeft: 8 }}
                    onClick={() => rejectInvite(i.id)}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* INVITED COHORTS */}
      {tab === "invited" &&
        (invitedLoading ? (
          <p>Loading invited cohorts…</p>
        ) : invitedCohorts.length === 0 ? (
          <p style={{ color: "#64748b" }}>No invited cohorts.</p>
        ) : (
          invitedCohorts.map((c) => (
            <div
              key={c.id}
              className="card"
              title="You are a member of this cohort."
              style={{
                padding: 22,
                marginBottom: 18,
                borderRadius: 14,
                boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16 }}>{c.name}</div>
              {c.description && (
                <div style={{ fontSize: 13, color: "#64748b" }}>{c.description}</div>
              )}
              <button
                className="btn-secondary"
                title="Open this cohort workspace."
                style={{ marginTop: 12 }}
                onClick={() => navigate(`/evaluator/cohorts/${c.id}`)}
              >
                Open Cohort
              </button>
            </div>
          ))
        ))}

      {/* OWNER COHORTS */}
      {tab === "cohorts" && (
        <>
          <input
            type="text"
            placeholder="Search cohorts…"
            title="Search cohorts by name or description."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              marginBottom: 24,
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #cbd5e1",
            }}
          />

          <div
            className="card"
            style={{
              padding: 24,
              marginBottom: 32,
              borderRadius: 14,
              background: "linear-gradient(180deg,#ffffff,#f8fafc)",
            }}
          >
            <h3 title="Create a new cohort to group startups and evaluators.">
              Create new cohort
            </h3>

            <input
              type="text"
              placeholder="Cohort name"
              title="Internal name for your cohort."
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: "100%", marginTop: 12 }}
            />

            <input
              type="text"
              placeholder="Optional description"
              title="Describe the purpose or focus of this cohort."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ width: "100%", marginTop: 12 }}
            />

            {error && <p style={{ color: "red" }}>{error}</p>}

            <button
              className="btn-primary"
              title="Create this cohort."
              onClick={createCohort}
              disabled={creating}
              style={{ marginTop: 16 }}
            >
              {creating ? "Creating..." : "Create Cohort"}
            </button>
          </div>

          {ownerLoading ? (
            <p>Loading cohorts…</p>
          ) : filteredOwner.length === 0 ? (
            <p style={{ color: "#64748b" }}>No cohorts found.</p>
          ) : (
            filteredOwner.map((c) => (
              <div
                key={c.id}
                className="card"
                title="You own and manage this cohort."
                style={{
                  padding: 20,
                  marginBottom: 16,
                  display: "flex",
                  justifyContent: "space-between",
                  borderRadius: 14,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
                  transition: "transform 0.15s",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{c.name}</div>
                  {c.description && (
                    <div style={{ fontSize: 13, color: "#64748b" }}>{c.description}</div>
                  )}
                </div>

                <div>
                  <button
                    className="btn-secondary"
                    title="Open and manage this cohort."
                    style={{ marginRight: 8 }}
                    onClick={() => navigate(`/evaluator/cohorts/${c.id}`)}
                  >
                    Open
                  </button>
                  <button
                    className="btn-secondary"
                    title="Delete this cohort. Startups will not be deleted."
                    onClick={() => deleteCohort(c.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}

export default Cohorts;
